import {
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { PlayArrow, Stop } from "@material-ui/icons";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { AudioDonationAttributes } from "../parse";

const useStyles = makeStyles((theme) => ({
  listItem: {
    paddingRight: 100,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  statusChip: {
    marginRight: theme.spacing(1),
  },
}));

type AdminAudioListItemProps = {
  donation: AudioDonationAttributes;
  buttons?: React.ReactNode;
  secondaryText?: React.ReactNode;
};

export const AdminAudioListItem = observer<AdminAudioListItemProps>(
  function AdminAudioListItem({ donation, buttons, secondaryText }) {
    const classes = useStyles();

    const [playingAudio, setPlayingAudio] = useState<string | null>(null);
    const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
      null,
    );
    const handlePlayAudio = (audioUrl: string, donationId: string) => {
      // If already playing this audio, stop it
      if (playingAudio === donationId) {
        audioElement?.pause();
        setPlayingAudio(null);
        return;
      }

      // Stop any currently playing audio
      if (audioElement) {
        audioElement.pause();
      }

      // Create and play new audio
      const audio = new Audio(audioUrl);
      audio.onended = () => setPlayingAudio(null);
      audio.play();
      setAudioElement(audio);
      setPlayingAudio(donationId);
    };

    function handleAddToDataset(objectId: any): void {
      throw new Error("Function not implemented.");
    }

    function handleAddToGame(objectId: any): void {
      throw new Error("Function not implemented.");
    }

    function handleDelete(objectId: any): void {
      throw new Error("Function not implemented.");
    }

    return (
      <ListItem key={donation.id} className={classes.listItem}>
        <ListItemText
          primary={
            <Box>
              <Box display="flex" alignItems="center">
                {donation.audio?.url && (
                  <IconButton
                    size="small"
                    color={
                      playingAudio === donation.id ? "secondary" : "default"
                    }
                    onClick={() =>
                      donation.audio &&
                      handlePlayAudio(donation.audio.url, donation.id)
                    }
                    title={playingAudio === donation.id ? t("stop") : t("play")}
                  >
                    {playingAudio === donation.id ? <Stop /> : <PlayArrow />}
                  </IconButton>
                )}
                <Typography
                  variant="body1"
                  component="span"
                  style={{ marginLeft: 10 }}
                >
                  {donation.audio?.name}
                </Typography>
              </Box>
              <Box mt={1}>
                <Chip
                  size="small"
                  label={donation.synthetic ? t("synthetic") : t("real")}
                  color={donation.synthetic ? "secondary" : "primary"}
                  className={classes.statusChip}
                />
                {donation.dataset && (
                  <Chip
                    size="small"
                    label={t("in-dataset")}
                    color="primary"
                    variant="outlined"
                    className={classes.statusChip}
                  />
                )}
                {donation.game && (
                  <Chip
                    size="small"
                    label={t("in-game")}
                    color="secondary"
                    variant="outlined"
                    className={classes.statusChip}
                  />
                )}
              </Box>
              <Box className={classes.actionButtons} mt={1}>
                {buttons}
              </Box>
            </Box>
          }
          secondary={secondaryText}
        />
      </ListItem>
    );
  },
);
